import * as React from 'react';
import { ArrowRight, Check, CirclePlay } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { resolveOnboardingIcon } from '@/components/onboarding-v2/onboardingIcons';
import { OverflowPills } from '@/components/dashboard/OverflowPills';

export interface SetupStepCta {
  label: string;
  /** Fired on click — open a dialog, navigate, etc. */
  onClick: () => void;
  disabled?: boolean;
  /** Accessible name when the visible label is generic (e.g. See the experience). */
  ariaLabel?: string;
  testId?: string;
  /** Show the play glyph — used for experience / guide CTAs. */
  showPlayIcon?: boolean;
}

export interface SetupStep {
  title: string;
  /** Whether this step is complete — drives the tick, strikethrough and bar. */
  done: boolean;
  /** Action button(s). First is the primary filled CTA; further entries become outline. */
  ctas: SetupStepCta[];
  /** Brand mark image (WhatsApp / Instagram SVGs). Takes precedence over `icon`. */
  iconSrc?: string;
  /** lucide-react icon name from the onboarding catalog when no `iconSrc`. */
  icon?: string;
  /** Capability chips under the title. */
  features?: readonly string[];
  /** Optional dedicated secondary CTA (e.g. See the experience). */
  secondaryCta?: SetupStepCta;
  /** Light-green "Recommended" chip — WhatsApp Automation only. */
  recommended?: boolean;
}

interface SetupProgressProps {
  steps: SetupStep[];
  /** Rough time-to-complete copy shown next to the step count (default header only). */
  totalTimeLabel?: string;
  /** Extra classes for the root card — e.g. `h-full` / `flex-1` for layout. */
  className?: string;
  /** Optional custom heading — replaces the default "Setup progress" eyebrow. */
  title?: string;
  /** Optional custom subheading — under `title` in the gray tray (or the default header). */
  description?: string;
  /** Hide the card header entirely (page already has its own title). */
  hideHeader?: boolean;
  /** Drop the outer white card chrome — step cards sit directly on the page. */
  unframed?: boolean;
  /** Renders in the gray tray opposite `title` (e.g. Book a demo). */
  headerAction?: React.ReactNode;
}

/**
 * Onboarding checklist card. Purely presentational — every step's `done` state
 * is derived by the parent from live app data.
 *
 * Incomplete rows render as article cards (icon, chips, primary CTA). Completed
 * rows keep a compact green check row.
 *
 * Grid columns are driven by how many cards are still PENDING (done rows sit
 * on `col-span-full`). Desktop is always a 3-column track — a lone / pair of
 * cards stay the same width as when three fit; extras wrap to the next row.
 * Mobile stays a single full-width column.
 */
export function setupProgressPendingCols(pending: number): string {
  if (pending <= 0) return 'grid-cols-1';
  return 'grid-cols-1 sm:grid-cols-3';
}

export function SetupProgress({
  steps,
  totalTimeLabel = 'about 5 minutes total',
  className,
  title,
  description,
  hideHeader = false,
  unframed = false,
  headerAction,
}: SetupProgressProps) {
  const total = steps.length;
  const done = steps.filter((s) => s.done).length;
  const pending = total - done;
  const useCustomHeader = Boolean(title || description);
  const pendingCols = setupProgressPendingCols(pending);
  const trayHeader = Boolean(title || description || headerAction);
  const headerVisible = !hideHeader && !trayHeader;

  return (
    <div
      className={cn(
        !unframed &&
          'rounded-2xl border border-[#E4E7EC] bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04)] sm:p-5',
        className
      )}
    >
      {headerVisible && (
        <div className="flex flex-wrap items-center gap-4 justify-between">
          <div className="min-w-0">
            {useCustomHeader ? (
              <>
                <h2 className="text-[18px] font-semibold tracking-[-0.02em] text-[#101828] sm:text-[20px]">
                  {title}
                </h2>
                {description && (
                  <p className="mt-1 text-[13.5px] text-[#667085]">{description}</p>
                )}
              </>
            ) : (
              <>
                <p className="text-[13px] font-semibold uppercase tracking-wide" style={{ color: '#0F7A3D' }}>
                  Setup progress
                </p>
                <p className="text-[15px] text-[#101828] mt-0.5">
                  <span className="font-semibold">
                    {done} of {total}
                  </span>{' '}
                  steps done · <span className="text-[#6B7280]">{totalTimeLabel}</span>
                </p>
              </>
            )}
          </div>
        </div>
      )}

      <div
        className={cn(
          'rounded-2xl bg-[#F2F4F7] p-5',
          headerVisible && 'mt-4'
        )}
      >
        {trayHeader && (
          <div className="mb-5 flex items-center justify-between gap-2 sm:gap-3">
            {title || description ? (
              <div className="min-w-0 flex-1">
                {title ? (
                  <h1 className="text-[24px] font-semibold tracking-[-0.03em] text-[#101828] sm:text-[28px]">
                    {title}
                  </h1>
                ) : null}
                {description ? (
                  <p className="mt-0.5 text-[14px] leading-snug text-[#667085] sm:text-[15px]">
                    {description}
                  </p>
                ) : null}
              </div>
            ) : (
              <span className="min-w-0 flex-1" />
            )}
            {headerAction}
          </div>
        )}
        <div
          data-testid="setup-progress-grid"
          className={cn('grid gap-3', pendingCols)}
        >
          {steps.map((step, i) =>
            step.done ? (
              <CompletedStepRow key={step.title} step={step} index={i} />
            ) : (
              <SetupStepCard key={step.title} step={step} index={i} compact={pending >= 3} />
            )
          )}
        </div>
      </div>
    </div>
  );
}

function StepIcon({ step, index }: { step: SetupStep; index: number }) {
  const [imageFailed, setImageFailed] = React.useState(false);

  React.useEffect(() => {
    setImageFailed(false);
  }, [step.iconSrc]);

  if (step.iconSrc && !imageFailed) {
    return (
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#F2F4F7] text-[#475467]">
        <img
          src={step.iconSrc}
          alt=""
          className="h-6 w-6 object-contain"
          onError={() => setImageFailed(true)}
        />
      </span>
    );
  }

  if (step.icon) {
    const Icon = resolveOnboardingIcon(step.icon);
    return (
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#F2F4F7] text-[#475467]">
        <Icon className="h-5 w-5" aria-hidden />
      </span>
    );
  }

  return (
    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[#E4E7EC] bg-[#F8FAFB] text-[13px] font-semibold text-[#475467]">
      {index + 1}
    </span>
  );
}

function SetupStepCard({
  step,
  index,
  compact = false,
}: {
  step: SetupStep;
  index: number;
  compact?: boolean;
}) {
  const primary = step.ctas[0];
  const secondary =
    step.secondaryCta ??
    (step.ctas[1]
      ? { ...step.ctas[1], showPlayIcon: step.ctas[1].showPlayIcon ?? false }
      : undefined);

  const pad = compact ? 'p-5 sm:p-4' : 'p-5';
  const recommendedBadge = step.recommended ? (
    <span
      data-testid="setup-step-recommended"
      className="absolute -top-2.5 right-[18px] z-10 inline-flex shrink-0 items-center rounded-md px-2 py-0.5 text-[11px] font-semibold text-white shadow-[0_1px_2px_rgba(16,24,40,0.12)]"
      style={{ background: 'var(--main-color)' }}
    >
      Recommended
    </span>
  ) : null;
  const body = (
    <>
      {/* Icon left; title + pills stack in the column beside it so the chips
          line up under the heading, not under the icon. */}
      <div className="flex items-start gap-3.5">
        <StepIcon step={step} index={index} />
        <div className="min-w-0 flex-1">
          <h3 className="text-[15px] font-semibold text-[#101828]">{step.title}</h3>
          {step.features && step.features.length > 0 && (
            <div className="mt-2 min-w-0">
              <OverflowPills
                items={step.features}
                label={`${step.title} capabilities`}
              />
            </div>
          )}
        </div>
      </div>

      {primary && (
        <div className="mt-auto grid grid-cols-1 gap-2 pt-4">
          <Button
            type="button"
            onClick={primary.onClick}
            disabled={primary.disabled}
            data-testid={primary.testId}
            aria-label={primary.ariaLabel}
            variant="outline"
            className="!h-11 w-full justify-center gap-1.5 !rounded-xl !border-[#EEF8F3] !bg-[#EEF8F3] px-3 !text-[14px] font-semibold !text-[#0BAB5F] shadow-none hover:!border-transparent hover:!text-white hover:brightness-110 hover:[background:var(--main-color)] [&_svg]:!size-3.5"
          >
            {primary.label}
            <ArrowRight className="h-3.5 w-3.5" />
          </Button>

          {secondary && (
            <Button
              type="button"
              variant="outline"
              onClick={secondary.onClick}
              disabled={secondary.disabled}
              data-testid={secondary.testId}
              aria-label={secondary.ariaLabel ?? secondary.label}
              className="h-9 w-full justify-center gap-1.5 border-[#B7DFC6] px-3 !text-[14px] text-[#0F8A48] hover:bg-[#F5FCF7] hover:text-[#0F8A48]"
            >
              {secondary.showPlayIcon ? <CirclePlay className="h-3.5 w-3.5" /> : null}
              {secondary.label}
            </Button>
          )}
        </div>
      )}
    </>
  );

  if (step.recommended) {
    return (
      <article
        data-testid="setup-step-recommended-card"
        className="relative flex h-full min-w-0 flex-col"
      >
        {recommendedBadge}
        <div className="setup-step-recommended-beam relative flex h-full min-w-0 flex-1 flex-col overflow-hidden rounded-2xl p-[2.5px] shadow-[0_2px_8px_rgba(16,24,40,0.04)]">
          <span
            className="setup-step-recommended-beam__spin"
            data-testid="setup-step-recommended-beam"
            aria-hidden
          />
          <div
            className={cn(
              'relative z-[1] flex h-full min-w-0 flex-1 flex-col rounded-[13.5px] bg-white',
              pad
            )}
          >
            {body}
          </div>
        </div>
      </article>
    );
  }

  return (
    <article
      className={cn(
        'flex h-full min-w-0 flex-col rounded-2xl bg-white shadow-[0_2px_8px_rgba(16,24,40,0.04)]',
        pad
      )}
    >
      {body}
    </article>
  );
}

function CompletedStepRow({ step, index }: { step: SetupStep; index: number }) {
  return (
    <div className="col-span-full flex items-center gap-3 rounded-2xl border border-[#D0F0DC] bg-[#F4FBF6] px-4 py-3.5">
      <span
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-white"
        style={{ backgroundColor: 'var(--secondary-color)' }}
        aria-hidden
      >
        <Check className="h-4 w-4" strokeWidth={3} />
      </span>
      <span
        className="min-w-0 flex-1 text-[14px] font-medium line-through"
        style={{ color: '#0F7A3D', textDecorationColor: 'rgba(26,177,105,0.4)' }}
      >
        {step.title}
      </span>
      <span className="sr-only">Step {index + 1} complete</span>
    </div>
  );
}
